// ─────────────────────────────────────────────────────────────────────────────
// widget_container.ts — Container types for SelectionWidgets
//
// Three container types manage widgets in different contexts:
//
//   SelectionContainer (abstract base)
//     ├── FixedBarContainer  — structural page UI (layer bar, color swatches)
//     ├── PopDialogContainer — floating menus (stylus menu, context menus)
//     └── BlockContainer     — page-insert blocks (settings, adventure choices)
//
// All containers share:
//   • drawAllowed: boolean — whether strokes can pass through or are consumed
//   • priority: number — higher priority containers consume strokes first
//   • analyzeStroke() — delegates to child widgets, returns result + container
//   • draw() — renders all child widgets
// ─────────────────────────────────────────────────────────────────────────────

import {
  SelectionWidget,
  type WidgetItem,
  type WidgetSelectionResult,
  type GestureKind,
} from './selection_widget';

// ── Base container ───────────────────────────────────────────────────────────

export interface ContainerStrokeResult {
  /** The selection result from the widget (null = passthrough/dissolve). */
  selection: WidgetSelectionResult | null;
  /** The widget that handled the stroke. */
  widget: SelectionWidget;
  /** The container that owns the widget. */
  container: SelectionContainer;
  /** Whether the stroke should be dissolved (true if it crossed a protected area). */
  dissolve: boolean;
}

export abstract class SelectionContainer {
  /**
   * If false, strokes that cross this container's area are dissolved.
   * If true, strokes pass through to the ink layer (no protection).
   */
  drawAllowed = true;

  /**
   * Priority for stroke consumption.  Higher values are checked first.
   * Pop dialogs > fixed bars > block containers (typical ordering).
   */
  priority = 0;

  /** Whether this container is currently active and should participate in stroke analysis. */
  abstract get active(): boolean;

  /**
   * Check whether a stroke crosses any widget in this container.
   * Returns true on first hit — the caller should then call analyzeStroke.
   */
  abstract strokeCrossesAny(pts: Array<{ x: number; y: number }>): boolean;

  /**
   * Full stroke analysis against all child widgets.
   * Returns a result if a widget claimed the stroke, or null if no widget
   * intersected (in which case the stroke was not within this container).
   *
   * When drawAllowed is false and the stroke crosses the container bbox
   * but no widget claims it, the result has dissolve: true and selection: null.
   */
  abstract analyzeContainerStroke(pts: Array<{ x: number; y: number }>): ContainerStrokeResult | null;

  /** Draw all child widget visuals. */
  abstract draw(ctx: CanvasRenderingContext2D, ox: number, oy: number): void;
}

// ── FixedBarContainer ────────────────────────────────────────────────────────
// For structural page UI: layer spots, color swatches, section color spots.
// Holds a single widget, synced each frame from renderer output.

export class FixedBarContainer extends SelectionContainer {
  readonly widget = new SelectionWidget();

  /** Label for debugging / identification. */
  label = '';

  constructor(label?: string) {
    super();
    this.drawAllowed = false;  // fixed bars protect their area by default
    this.priority = 10;
    if (label) this.label = label;
  }

  get active(): boolean {
    return this.widget.items.length > 0;
  }

  /** Convenience: replace items and recompute bbox. */
  updateItems(items: WidgetItem[]): void {
    this.widget.updateItems(items);
  }

  preSelect(group: number, itemIndex: number): void {
    this.widget.preSelect(group, itemIndex);
  }

  hasRing(group: number): boolean {
    return this.widget.hasRing(group);
  }

  clear(): void {
    this.widget.clear();
  }

  get animating(): boolean {
    return this.widget.animating;
  }

  strokeCrossesAny(pts: Array<{ x: number; y: number }>): boolean {
    return this.widget.strokeCrossesRegion(pts);
  }

  analyzeContainerStroke(pts: Array<{ x: number; y: number }>): ContainerStrokeResult | null {
    if (!this.widget.strokeCrossesRegion(pts)) return null;

    const sel = this.widget.analyzeStroke(pts);
    return {
      selection: sel,
      widget: this.widget,
      container: this,
      dissolve: !this.drawAllowed && sel === null,
    };
  }

  draw(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    this.widget.draw(ctx, ox, oy);
  }
}

// ── BlockContainer ───────────────────────────────────────────────────────────
// For insert blocks on pages: settings view, adventure choices, etc.
// Holds multiple widgets, each handling a row/section of the block.

export class BlockContainer extends SelectionContainer {
  private _widgets: SelectionWidget[] = [];
  private _active = false;

  /** Combined bounding box of all child widgets (page-local). */
  bbox = { x: 0, y: 0, w: 0, h: 0 };

  constructor() {
    super();
    this.drawAllowed = false;  // blocks protect their area by default
    this.priority = 5;
  }

  get active(): boolean { return this._active; }
  set active(v: boolean) { this._active = v; }

  get widgets(): readonly SelectionWidget[] { return this._widgets; }

  /** Add a widget to this block container. Returns it for chaining. */
  addWidget(label?: string): SelectionWidget {
    const w = new SelectionWidget();
    this._widgets.push(w);
    return w;
  }

  /** Remove all widgets. */
  clearWidgets(): void {
    this._widgets = [];
    this.bbox = { x: 0, y: 0, w: 0, h: 0 };
  }

  /** Rebuild the combined bbox from all child widget bboxes. */
  recomputeBbox(): void {
    if (this._widgets.length === 0) {
      this.bbox = { x: 0, y: 0, w: 0, h: 0 };
      return;
    }
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const w of this._widgets) {
      if (w.items.length === 0) continue;
      x0 = Math.min(x0, w.bbox.x);
      y0 = Math.min(y0, w.bbox.y);
      x1 = Math.max(x1, w.bbox.x + w.bbox.w);
      y1 = Math.max(y1, w.bbox.y + w.bbox.h);
    }
    if (x0 === Infinity) {
      this.bbox = { x: 0, y: 0, w: 0, h: 0 };
    } else {
      this.bbox = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    }
  }

  strokeCrossesAny(pts: Array<{ x: number; y: number }>): boolean {
    for (const w of this._widgets) {
      if (w.strokeCrossesRegion(pts)) return true;
    }
    return false;
  }

  analyzeContainerStroke(pts: Array<{ x: number; y: number }>): ContainerStrokeResult | null {
    for (const w of this._widgets) {
      if (!w.strokeCrossesRegion(pts)) continue;
      const sel = w.analyzeStroke(pts);
      return {
        selection: sel,
        widget: w,
        container: this,
        dissolve: !this.drawAllowed && sel === null,
      };
    }
    // Check if stroke crosses the combined bbox even if no widget claimed it
    if (!this.drawAllowed) {
      const b = this.bbox;
      const m = 8;
      for (const p of pts) {
        if (p.x >= b.x - m && p.x <= b.x + b.w + m &&
            p.y >= b.y - m && p.y <= b.y + b.h + m) {
          // Stroke is in the block area but no widget claimed it — dissolve
          return {
            selection: null,
            widget: this._widgets[0] ?? new SelectionWidget(),
            container: this,
            dissolve: true,
          };
        }
      }
    }
    return null;
  }

  draw(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    for (const w of this._widgets) {
      w.draw(ctx, ox, oy);
    }
  }
}
